import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const AMENITY_CONFIGS = [
  { icon: 'ri-wifi-line', titleKey: 'amen_wifi_title', descKey: 'amen_wifi_desc', detailKey: 'amen_wifi_detail', image: 'https://public.readdy.ai/ai/img_res/9b0526d7472036a872dcab309661f7e5.jpg' },
  { icon: 'ri-temp-cold-line', titleKey: 'amen_ac_title', descKey: 'amen_ac_desc', detailKey: 'amen_ac_detail', image: 'https://readdy.ai/api/search-image?query=modern%20air%20conditioning%20vents%20inside%20a%20luxury%20premium%20coach%20bus%20interior%2C%20cool%20comfortable%20atmosphere%2C%20clean%20beige%20and%20white%20seats%2C%20professional%20photography%2C%20crisp%20climate%20controlled%20travel%20environment&width=800&height=600&seq=am_ac01&orientation=landscape' },
  { icon: 'ri-armchair-line', titleKey: 'amen_seats_title', descKey: 'amen_seats_desc', detailKey: 'amen_seats_detail', image: 'https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/fc680a3c-0d78-4629-aa68-c69ab2c22107_Screenshot-2026-03-27-202940.png?v=8e4b6ba0dd68693a811992aecab9cc8b' },
  { icon: 'ri-sofa-line', titleKey: 'amen_recline_title', descKey: 'amen_recline_desc', detailKey: 'amen_recline_detail', image: 'https://readdy.ai/api/search-image?query=fully%20reclined%20premium%20luxury%20bus%20seat%20interior%2C%20plush%20comfortable%20seating%2C%20warm%20beige%20and%20dark%20tones%2C%20upscale%20coach%20interior%2C%20travel%20comfort%2C%20close%20up%20professional%20photography%20of%20reclining%20chair%20in%20motion&width=800&height=600&seq=am_recline01&orientation=landscape' },
  { icon: 'ri-tv-2-line', titleKey: 'amen_screen_title', descKey: 'amen_screen_desc', detailKey: 'amen_screen_detail', image: 'https://readdy.ai/api/search-image?query=individual%20entertainment%20touchscreen%20on%20the%20back%20of%20a%20luxury%20bus%20seat%20showing%20a%20movie%2C%20dark%20premium%20bus%20interior%2C%20modern%20technology%2C%20warm%20ambient%20cabin%20lighting%2C%20professional%20travel%20photography%2C%20vibrant%20display&width=800&height=600&seq=am_screen01&orientation=landscape' },
  { icon: 'ri-battery-charge-line', titleKey: 'amen_usb_title', descKey: 'amen_usb_desc', detailKey: 'amen_usb_detail', image: 'https://readdy.ai/api/search-image?query=USB%20charging%20port%20on%20a%20premium%20bus%20seat%20armrest%2C%20modern%20luxury%20bus%20interior%20detail%2C%20close%20up%20photography%2C%20sleek%20design%2C%20warm%20neutral%20tones%2C%20high%20quality%20travel%20amenity%2C%20cable%20plugged%20in%20charging%20phone&width=800&height=600&seq=am_usb01&orientation=landscape' },
  { icon: 'ri-luggage-cart-line', titleKey: 'amen_luggage_title', descKey: 'amen_luggage_desc', detailKey: 'amen_luggage_detail', image: 'https://readdy.ai/api/search-image?query=spacious%20clean%20luggage%20storage%20compartment%20underneath%20a%20luxury%20coach%20bus%2C%20large%20capacity%2C%20well%20organized%20suitcases%20neatly%20placed%2C%20professional%20photography%2C%20modern%20premium%20transport%20vehicle%2C%20warm%20lighting&width=800&height=600&seq=am_luggage01&orientation=landscape' },
];

const GALLERY_CONFIGS = [
  { id: 'g1', labelKey: 'amen_gal1_label', captionKey: 'amen_gal1_caption', image: 'https://readdy.ai/api/search-image?query=wide%20angle%20view%20of%20luxurious%20premium%20coach%20bus%20cabin%20interior%20with%20rows%20of%20leather%20reclining%20seats%2C%20golden%20ambient%20lighting%2C%20individual%20headrests%2C%20clean%20modern%20design%2C%20high%20end%20intercity%20travel%2C%20cinematic%20professional%20photography&width=1200&height=800&seq=gal_cabin01&orientation=landscape' },
  { id: 'g2', labelKey: 'amen_gal2_label', captionKey: 'amen_gal2_caption', image: 'https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/fc680a3c-0d78-4629-aa68-c69ab2c22107_Screenshot-2026-03-27-202940.png?v=8e4b6ba0dd68693a811992aecab9cc8b' },
  { id: 'g3', labelKey: 'amen_gal3_label', captionKey: 'amen_gal3_caption', image: 'https://readdy.ai/api/search-image?query=close%20up%20of%20individual%20entertainment%20touchscreen%20mounted%20on%20the%20back%20of%20a%20luxury%20bus%20seat%20showing%20a%20film%2C%20dark%20premium%20bus%20cabin%2C%20vibrant%20display%20glowing%2C%20warm%20cabin%20ambient%20lighting%2C%20modern%20travel%20technology%2C%20professional%20photography&width=900&height=700&seq=gal_screen01&orientation=landscape' },
  { id: 'g4', labelKey: 'amen_gal4_label', captionKey: 'amen_gal4_caption', image: 'https://readdy.ai/api/search-image?query=close%20detail%20of%20USB%20charging%20ports%20on%20a%20premium%20bus%20seat%20armrest%2C%20modern%20luxury%20coach%20interior%2C%20cable%20plugged%20in%2C%20soft%20warm%20lighting%2C%20sleek%20minimal%20design%2C%20high%20quality%20travel%20amenity%20photography&width=900&height=700&seq=gal_usb01&orientation=landscape' },
  { id: 'g5', labelKey: 'amen_gal5_label', captionKey: 'amen_gal5_caption', image: 'https://readdy.ai/api/search-image?query=overhead%20view%20of%20a%20luxury%20coach%20bus%20interior%20aisle%20with%20individual%20air%20conditioning%20vents%20above%20each%20seat%2C%20bright%20clean%20interior%2C%20modern%20premium%20intercity%20bus%2C%20crisp%20cool%20atmosphere%2C%20professional%20architectural%20photography&width=1000&height=700&seq=gal_ac01&orientation=landscape' },
  { id: 'g6', labelKey: 'amen_gal6_label', captionKey: 'amen_gal6_caption', image: 'https://readdy.ai/api/search-image?query=open%20underfloor%20luggage%20compartment%20of%20a%20modern%20luxury%20coach%20bus%20with%20neatly%20arranged%20suitcases%20inside%2C%20bright%20clean%20storage%20area%2C%20professional%20vehicle%20photography%2C%20well%20organised%20travel%20luggage%2C%20sleek%20modern%20coach%20exterior&width=900&height=700&seq=gal_luggage01&orientation=landscape' },
];

const STAT_CONFIGS = [
  { icon: 'ri-star-line', valKey: 'amen_stat1_val', labelKey: 'amen_stat1_label' },
  { icon: 'ri-shield-check-line', valKey: 'amen_stat2_val', labelKey: 'amen_stat2_label' },
  { icon: 'ri-map-pin-line', valKey: 'amen_stat3_val', labelKey: 'amen_stat3_label' },
];

function AmenityCard({ amenity, index }: { amenity: typeof AMENITY_CONFIGS[number]; index: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${(index % 3) * 100}ms` }}
    >
      <div className="group bg-white rounded-2xl overflow-hidden border border-[#EDE9E3] hover:border-[#C9A84C]/40 transition-all duration-300 hover:-translate-y-1">
        <div className="h-56 overflow-hidden relative">
          <img
            src={amenity.image}
            alt={t(amenity.titleKey)}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#C9A84C]/10 rounded-xl">
              <i className={`${amenity.icon} text-[#C9A84C] text-xl`} />
            </div>
            <h3 className="font-playfair text-xl font-bold text-[#1a1a1a]">{t(amenity.titleKey)}</h3>
          </div>
          <p className="text-[#777] text-sm leading-relaxed mb-4">{t(amenity.descKey)}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest cursor-pointer hover:gap-2.5 transition-all duration-200 whitespace-nowrap"
          >
            {expanded ? t('amen_showLess') : t('amen_learnMore')}
            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
          </button>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-[#EDE9E3] text-sm text-[#555] leading-relaxed">
              {t(amenity.detailKey)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] overflow-hidden">
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #C9A84C 0%, transparent 50%), radial-gradient(circle at 75% 50%, #C9A84C 0%, transparent 50%)' }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
        <button
          onClick={() => window.REACT_APP_NAVIGATE?.('/')}
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9A84C] text-sm font-medium tracking-wider uppercase transition-colors duration-300 cursor-pointer mb-8 group"
        >
          <span className="w-7 h-7 flex items-center justify-center rounded-full border border-white/20 group-hover:border-[#C9A84C] transition-colors duration-300">
            <i className="ri-arrow-left-line text-sm" />
          </span>
          {t('common_backHome')}
        </button>
        <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.35em] uppercase mb-5">{t('amen_eyebrow')}</p>
        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
          {t('amen_title1')} <span className="text-[#C9A84C]">{t('amen_title2')}</span>
        </h1>
        <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
          {t('amen_subtitle')}
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-8 md:gap-14">
          {STAT_CONFIGS.map((stat) => (
            <div key={stat.labelKey} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C9A84C]/15 rounded-full">
                <i className={`${stat.icon} text-[#C9A84C] text-lg`} />
              </div>
              <div className="text-left">
                <div className="text-white font-bold text-lg font-playfair">{t(stat.valKey)}</div>
                <div className="text-white/50 text-xs uppercase tracking-wider">{t(stat.labelKey)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuickNavBar() {
  const { t } = useTranslation();
  return (
    <div className="sticky top-0 z-40 bg-white/95 border-b border-[#EDE9E3] backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
          {AMENITY_CONFIGS.map((a) => (
            <button
              key={a.titleKey}
              onClick={() => {
                const id = `amenity-${a.titleKey.replace(/_/g, '-').toLowerCase()}`;
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F8F5F0] hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] text-[#555] text-xs font-semibold uppercase tracking-wider transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className={`${a.icon} text-sm`} />
              {t(a.titleKey)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function InteriorGallery() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={`w-full bg-[#1a1a1a] py-20 lg:py-28 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.35em] uppercase mb-4">{t('amen_gallery_eyebrow')}</p>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('amen_gallery_title1')} <span className="text-[#C9A84C]">{t('amen_gallery_title2')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto leading-relaxed">{t('amen_gallery_subtitle')}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="w-full lg:flex-1">
            <div
              className="relative rounded-2xl overflow-hidden cursor-zoom-in group"
              onClick={() => setLightbox(true)}
              style={{ height: '480px' }}
            >
              <img
                src={GALLERY_CONFIGS[active].image}
                alt={t(GALLERY_CONFIGS[active].labelKey)}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.25em] uppercase mb-1">{t(GALLERY_CONFIGS[active].labelKey)}</p>
                <p className="text-white/80 text-sm leading-relaxed">{t(GALLERY_CONFIGS[active].captionKey)}</p>
              </div>
              <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-zoom-in-line text-white text-base" />
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setActive((active - 1 + GALLERY_CONFIGS.length) % GALLERY_CONFIGS.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-[#C9A84C] rounded-full text-white transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setActive((active + 1) % GALLERY_CONFIGS.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-[#C9A84C] rounded-full text-white transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>

          <div className="w-full lg:w-52 flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {GALLERY_CONFIGS.map((item, idx) => (
              <button
                key={item.id}
                onClick={() => setActive(idx)}
                className={`relative shrink-0 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                  active === idx ? 'ring-2 ring-[#C9A84C] opacity-100' : 'opacity-50 hover:opacity-80'
                }`}
                style={{ width: '110px', height: '80px' }}
              >
                <img src={item.image} alt={t(item.labelKey)} className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span className="absolute bottom-1.5 left-0 right-0 text-center text-white text-[9px] font-semibold uppercase tracking-wide px-1 leading-tight">
                  {t(item.labelKey)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-8">
          {GALLERY_CONFIGS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActive(idx)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                active === idx ? 'w-8 h-2 bg-[#C9A84C]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={GALLERY_CONFIGS[active].image}
              alt={t(GALLERY_CONFIGS[active].labelKey)}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="mt-4 text-center">
              <p className="text-[#C9A84C] text-xs tracking-[0.25em] uppercase mb-1">{t(GALLERY_CONFIGS[active].labelKey)}</p>
              <p className="text-white/70 text-sm">{t(GALLERY_CONFIGS[active].captionKey)}</p>
            </div>
            <button onClick={() => setLightbox(false)} className="absolute -top-4 -right-4 w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[#C9A84C] rounded-full text-white transition-all duration-200 cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
            <button onClick={() => setActive((active - 1 + GALLERY_CONFIGS.length) % GALLERY_CONFIGS.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-[#C9A84C] rounded-full text-white transition-all duration-200 cursor-pointer">
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button onClick={() => setActive((active + 1) % GALLERY_CONFIGS.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-[#C9A84C] rounded-full text-white transition-all duration-200 cursor-pointer">
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function CtaBanner() {
  const { t } = useTranslation();
  return (
    <section className="py-20 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(ellipse at center, #C9A84C 0%, transparent 70%)' }}
      />
      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.35em] uppercase mb-4">{t('amen_cta_eyebrow')}</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-5">{t('amen_cta_title')}</h2>
        <p className="text-white/60 text-base mb-9 leading-relaxed">{t('amen_cta_subtitle')}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => window.REACT_APP_NAVIGATE?.('/schedules')}
            className="px-8 py-3.5 bg-[#C9A84C] hover:bg-[#b8953e] text-white text-sm font-semibold tracking-wider uppercase rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('amen_cta_btn1')}
          </button>
          <button
            onClick={() => {
              window.REACT_APP_NAVIGATE?.('/');
              setTimeout(() => { document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }, 350);
            }}
            className="px-8 py-3.5 border-2 border-white/20 hover:border-[#C9A84C] text-white/70 hover:text-[#C9A84C] text-sm font-semibold tracking-wider uppercase rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('amen_cta_btn2')}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function AmenitiesPage() {
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <div className="min-h-screen bg-white font-lato">
      <Navbar />
      <HeroSection />
      <QuickNavBar />

      <section
        ref={gridRef as React.RefObject<HTMLElement>}
        className={`w-full bg-[#F8F5F0] py-20 lg:py-28 transition-all duration-700 ${gridVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {AMENITY_CONFIGS.map((amenity, index) => (
              <div
                key={amenity.titleKey}
                id={`amenity-${amenity.titleKey.replace(/_/g, '-').toLowerCase()}`}
                className={
                  index === AMENITY_CONFIGS.length - 1 && AMENITY_CONFIGS.length % 3 === 1
                    ? 'md:col-span-2 lg:col-span-1 lg:col-start-2'
                    : ''
                }
              >
                <AmenityCard amenity={amenity} index={index} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <InteriorGallery />
      <CtaBanner />
      <Footer />
    </div>
  );
}
